import { z } from 'zod';
import { baseComponentSchema } from '../component-base.schemas';
import { mediaTextMediaSchema } from './media-text.schemas';

// Form component schemas
export const formFieldValidationSchema = z
  .object({
    minLength: z.number().int().min(0).max(500).optional(),
    maxLength: z.number().int().min(1).max(5000).optional(),
    pattern: z.string().max(200, 'Pattern is too long').optional(), // Regex pattern
  })
  .optional();

export const formFieldSchema = z.object({
  id: z.string().uuid('Invalid field ID').optional(),
  label: z.string().min(1, 'Field label is required').max(100, 'Field label is too long'),
  identifier: z
    .string()
    .min(1, 'Field identifier is required')
    .max(50, 'Field identifier is too long')
    .regex(
      /^[a-zA-Z][a-zA-Z0-9_]*$/,
      'Field identifier must start with a letter and contain only letters, numbers, and underscores'
    ),
  type: z.enum(['text', 'email', 'tel', 'textarea', 'select', 'checkbox', 'radio']),
  placeholder: z.string().max(100, 'Placeholder is too long').optional(),
  required: z.boolean().default(false),
  options: z.array(z.string().min(1).max(100)).max(20, 'Too many options').optional(), // For select, checkbox, radio
  order: z.number().int().min(0).optional(),
  validation: formFieldValidationSchema,
});

// List of placeholder/invalid email domains that should not be used
const BLOCKED_EMAIL_DOMAINS = ['yoursite.com', 'example.com', 'test.com', 'placeholder.com'];

const isValidRecipientEmail = (email: string): boolean => {
  if (!email) return false;
  const domain = email.toLowerCase().split('@')[1];
  return !BLOCKED_EMAIL_DOMAINS.some(
    blocked => domain === blocked || domain?.endsWith(`.${blocked}`)
  );
};

// Email settings schema - toEmail is optional when saveSubmissions is enabled
export const emailSettingsSchema = z.object({
  fromEmail: z.string().email('Invalid from email address').optional().or(z.literal('')),
  toEmail: z
    .string()
    .email('Invalid to email address')
    .refine(isValidRecipientEmail, {
      message:
        'Please enter a real email address. Placeholder emails like yoursite.com or example.com are not allowed.',
    })
    .optional()
    .or(z.literal('')),
  subject: z.string().max(200, 'Email subject is too long').optional(),
  successMessage: z.string().max(300, 'Success message is too long').optional(),
  errorMessage: z.string().max(300, 'Error message is too long').optional(),
  redirectOnSuccess: z.boolean().optional(),
  redirectUrl: z.string().max(500, 'Redirect URL is too long').optional(),
});

export const formDataSchema = z.object({
  title: z.string().min(1, 'Form title is required').max(200, 'Form title is too long'),
  description: z.string().max(500, 'Form description is too long').optional(),
  fields: z
    .array(formFieldSchema)
    .min(1, 'At least one form field is required')
    .max(20, 'Too many form fields'),
  submitButton: z.object({
    text: z
      .string()
      .min(1, 'Submit button text is required')
      .max(50, 'Submit button text is too long'),
    style: z.enum(['primary', 'secondary', 'outline']).default('primary'),
    backgroundColor: z
      .string()
      .regex(/^#[0-9A-Fa-f]{6}$/, 'Invalid color format')
      .optional(),
    textColor: z
      .string()
      .regex(/^#[0-9A-Fa-f]{6}$/, 'Invalid color format')
      .optional(),
  }),
  emailSettings: emailSettingsSchema,
  // Save submissions to database (all plans supported with different limits, default false for backward compatibility)
  saveSubmissions: z.boolean().optional().default(false),
  styling: z.object({
    backgroundColor: z
      .string()
      .regex(/^#[0-9A-Fa-f]{6}$/, 'Invalid color format')
      .optional(),
    textColor: z
      .string()
      .regex(/^#[0-9A-Fa-f]{6}$/, 'Invalid color format')
      .optional(),
    borderColor: z
      .string()
      .regex(/^#[0-9A-Fa-f]{6}$/, 'Invalid color format')
      .optional(),
    spacing: z.enum(['compact', 'normal', 'spacious']).default('normal'),
    layout: z.enum(['single-column', 'two-column']).default('single-column'),
    alignment: z.enum(['left', 'center', 'right']).default('center'),
  }),
});

export const formComponentSchema = baseComponentSchema.extend({
  type: z.literal('form'),
  data: formDataSchema,
});

export const formSplitDataSchema = formDataSchema.extend({
  media: mediaTextMediaSchema,
  mediaPosition: z.enum(['left', 'right']).default('left'),
  stackOnMobile: z.boolean().optional().default(true),
});

export const formSplitComponentSchema = baseComponentSchema.extend({
  type: z.literal('form-split'),
  data: formSplitDataSchema,
});

// Type exports
export type FormField = z.infer<typeof formFieldSchema>;
export type FormFieldValidation = z.infer<typeof formFieldValidationSchema>;
export type EmailSettings = z.infer<typeof emailSettingsSchema>;
export type FormData = z.infer<typeof formDataSchema>;
export type FormComponent = z.infer<typeof formComponentSchema>;
export type FormSplitData = z.infer<typeof formSplitDataSchema>;
export type FormSplitComponent = z.infer<typeof formSplitComponentSchema>;
